// Third party
import React from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { VALUE_HOME, VALUE_END, VALUE_LEFT, VALUE_RIGHT } from 'keycode-js';

// Redux
import { ReduxState } from '../../../../redux/modules/types';
import { Announcement } from '../../../../redux/modules/types/announcements';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage, LOCALE } from '../../../../utils/i18n';

// Components
import { Button, CloseButton, DestructiveButton } from '../../buttons';
import { ModalAddons } from '../common';
import { ControlItem, Tabs } from '../../navigation';
import { IntegerValue } from '../../common';
import DeleteAnnouncementModal from './DeleteAnnouncementModal';
import AnnouncementParticipants from './AnnouncementParticipants';
import type { SelectItem } from '../../inputs/Select';
import { Select } from '../../inputs';
import Locale from '@/types/locale';
import NotificationBody from '../../notification/NotificationBody';
import Modal from '@/views/components/modals/AccessibleModal';

enum AnnouncementDetailsTabOption {
  Overview = 'OVERVIEW',
  Participants = 'PARTICIPANTS',
}

type StateProps = {
  announcement?: Announcement;
};

type Props = StateProps & {
  announcementId?: string;
  onCancel: () => void;
  onClosed: () => void;
  show?: boolean;
};

type State = {
  localAnnouncement?: Announcement;
  selectedTab: AnnouncementDetailsTabOption;
  selectedLocale?: SelectItem;
  showDeleteAnnouncementModal: boolean;
};

class AnnouncementDetailsModal extends React.Component<Props, State> {
  locales: Array<LOCALE> = [LOCALE.EN_CA, LOCALE.FR_CA];

  getLocales = () =>
    this.locales.map(locale => ({
      value: locale,
      label: formatMessage(`Common.Language.${locale}`),
    }));

  readonly state: State = {
    // Keeps a copy of the announcement being deleted local to this modal component
    // When the deletion operation is confirmed, this allows the deleted announcement to be
    // removed from the redux store but kept locally until the confirmation modal is closed.
    // This prevents any content flash while the modal is transitioning out
    localAnnouncement: this.props.announcement,
    selectedTab: AnnouncementDetailsTabOption.Overview,
    selectedLocale: this.getLocales()[0],
    showDeleteAnnouncementModal: false,
  };

  private tabRefs = [] as React.RefObject<HTMLButtonElement>[];

  componentDidUpdate(prevProps: Props) {
    const { announcement } = this.props;
    if (announcement && prevProps.announcement !== announcement) {
      this.setState({
        localAnnouncement: announcement,
        selectedLocale: this.getLocales()[0],
      });
      this.tabRefs = this.getTabs().map(() => React.createRef<HTMLButtonElement>());
    }
  }

  handleLocaleChange = (selectedItem?: SelectItem) => {
    this.setState({
      selectedLocale: selectedItem,
    });
  };

  getTabs = () => {
    const { announcement } = this.props;
    const tabs = [
      {
        value: AnnouncementDetailsTabOption.Overview,
        i18nKey: 'AnnouncementsAdmin.Details.Tabs.Overview',
      },
      {
        value: AnnouncementDetailsTabOption.Participants,
        i18nKey: 'AnnouncementsAdmin.Details.Tabs.Participants',
        hidden: !announcement?.acknowledgmentRequired,
      },
    ];

    return tabs.filter(tab => !tab.hidden);
  };

  closeModal = () => {
    const { onClosed } = this.props;

    this.setState({ selectedTab: AnnouncementDetailsTabOption.Overview });
    onClosed();
  };

  changeTab = (option: AnnouncementDetailsTabOption) => {
    this.setState({ selectedTab: option });
  };

  handleTabListKeyDown = (event: React.KeyboardEvent): void => {
    const { key } = event;
    const tabList = this.getTabs();
    const tabListLength = tabList.length;
    const currentTabIndex = tabList.findIndex(tab => tab.value === this.state.selectedTab);
    const acceptedKeys = [VALUE_HOME, VALUE_END, VALUE_LEFT, VALUE_RIGHT];

    if (!acceptedKeys.includes(key)) {
      return;
    }

    let newIndex = 0;

    switch (key) {
      case VALUE_HOME:
        newIndex = 0;
        break;
      case VALUE_END:
        newIndex = tabListLength - 1;
        break;
      case VALUE_LEFT:
        newIndex = (currentTabIndex - 1 + tabListLength) % tabListLength;
        break;
      case VALUE_RIGHT:
        newIndex = (currentTabIndex + 1 + tabListLength) % tabListLength;
        break;
    }

    this.changeTab(tabList[newIndex].value);
    this.tabRefs[newIndex].current?.focus();
  };

  render() {
    const { onCancel, show } = this.props;
    const { localAnnouncement, selectedTab, showDeleteAnnouncementModal, selectedLocale } = this.state;

    if (!localAnnouncement) {
      return null;
    }

    return (
      <>
        <Modal centered show={show} className="announcement-details-modal modal" onHide={onCancel} onExited={this.closeModal}>
          <Modal.Header>
            <Modal.Title>
              {formatLocalizedString(localAnnouncement.title, selectedLocale?.value as Locale)}
              {localAnnouncement.acknowledgmentRequired && <i className="icon-acknowledge" />}
            </Modal.Title>
            {!!onCancel && <CloseButton onClick={onCancel} />}
          </Modal.Header>
          <Modal.Body>
            {localAnnouncement.acknowledgmentRequired && (
              <ModalAddons.Nav className="tabs-acknowledgment">
                <ModalAddons.Tabs>
                  <Tabs role="tablist" onChange={this.changeTab} selected={selectedTab} onKeyDown={this.handleTabListKeyDown}>
                    {this.getTabs().map((tab, index) => (
                      <ControlItem
                        role="tab"
                        ref={this.tabRefs[index]}
                        key={tab.value}
                        value={tab.value}
                        aria-selected={tab.value === selectedTab}
                        aria-controls={tab.value}
                      >
                        <FormattedMessage id={tab.i18nKey} />
                      </ControlItem>
                    ))}
                  </Tabs>
                </ModalAddons.Tabs>
                {localAnnouncement.acknowledgmentRequired && (
                  <div className="aknowlegement-rate">
                    <span className="percentage">
                      <span className="value">
                        <IntegerValue
                          decimalPlace={1}
                          value={(Number(localAnnouncement.acknowledgmentCount) / Number(localAnnouncement.recipientCount)) * 100}
                          isPercentage
                        />
                      </span>
                      <FormattedMessage id="AnnouncementsAdmin.Details.AcknowledgmentRate.Acknowledged" />
                    </span>
                    <span className="details">
                      <span className="value">{localAnnouncement.acknowledgmentCount}</span> <FormattedMessage id="Common.Of" />{' '}
                      <span className="value">{localAnnouncement.recipientCount}</span>
                    </span>
                  </div>
                )}
              </ModalAddons.Nav>
            )}
            <div
              role="tabpanel"
              id={AnnouncementDetailsTabOption.Overview}
              className={classNames({ 'visually-hidden': selectedTab !== AnnouncementDetailsTabOption.Overview })}
            >
              <NotificationBody item={localAnnouncement} locale={selectedLocale?.value as Locale} totalText />
            </div>
            <div role="tabpanel" id={AnnouncementDetailsTabOption.Participants} className="d-flex flex-grow-1 flex-shrink-1">
              <AnnouncementParticipants
                announcementId={localAnnouncement._id}
                className={classNames({ 'visually-hidden': selectedTab !== AnnouncementDetailsTabOption.Participants })}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <DestructiveButton className="with-border" onClick={() => this.setState({ showDeleteAnnouncementModal: true })}>
              <FormattedMessage id="AnnouncementsAdmin.Details.Delete" />
            </DestructiveButton>
            <Select
              items={this.getLocales()}
              label={formatMessage('AnnouncementsAdmin.Details.Language')}
              hideLabel
              onChange={({ selectedItem }) => this.handleLocaleChange(selectedItem)}
              selectedItem={selectedLocale}
            >
              <button className="select-button">
                <i className="me-2 icon-cheveron-down" />
                {selectedLocale?.value.split('-', 1).join().toUpperCase()}
                <i className="ms-3 icon-globe" />
              </button>
            </Select>
            <Button onClick={onCancel}>
              <FormattedMessage id="Common.Close" />
            </Button>
          </Modal.Footer>
        </Modal>
        <DeleteAnnouncementModal
          announcementId={localAnnouncement._id}
          isInnerModal
          onCancel={() => this.setState({ showDeleteAnnouncementModal: false })}
          onDeleted={() => {
            this.setState({ showDeleteAnnouncementModal: false });
            onCancel();
          }}
          show={showDeleteAnnouncementModal}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState, ownProps: Props): StateProps => ({
  announcement: state.announcementsAdmin.announcements.find(announcement => announcement._id === ownProps.announcementId),
});

export default connect(mapStateToProps)(AnnouncementDetailsModal);
